import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatGuestOwnershipTransferNewMemberLocationState,
    NatSharer,
} from './newMember/NatGuestOwnershipTransferNewMemberInterface'
import {Nullable} from '../../../../app/TypeHelpers'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {PageTitle} from '../../applications/common/resident/NatResidentApplicationsCommon'
import {Box} from '@mui/material'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import NatGuestNewMemberOutlinePageForm from './newMember/NatGuestNewMemberOutlinePageForm'
import {NatOwnershipTransfersRepo} from '../repository/NatOwnershipTransfersRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {
    NatPutOwnershipTransferNewMemberOutlineRequest,
    NatPutOwnershipTransferNewMemberResponse,
} from '../repository/NatPutNewMemberRequest'
import {Messages} from '../../../../Messages'
import {GuestBackButton, GuestPlainLinkStyledBackButton} from '../common/NatOwnershipTransfersCommon'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId?: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatGuestOwnershipTransferNewMemberLocationState>
}

interface State {
    familyName: Nullable<string>
    firstName: Nullable<string>
    familyNameKana: Nullable<string>
    firstNameKana: Nullable<string>
    mailAddress: Nullable<string>
    mailAddressConfirm: Nullable<string>
    sharerList: NatSharer[]
    residentPlace: Nullable<string>
    ownershipTransferDate: Nullable<string>
    zipCode: Nullable<string>
    prefectureCd: Nullable<string>
    address2: Nullable<string>
    address3: Nullable<string>
    condominiumName: Nullable<string>
    roomNumber: Nullable<string>
    isLoadCompleted: boolean
}

class NatGuestNewMemberInputOutlinePage
    extends React.Component<Props & GuestWithErrorHandlingPagePropsSet, State> {
    constructor(props: Props & GuestWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            familyName: null,
            firstName: null,
            familyNameKana: null,
            firstNameKana: null,
            mailAddress: null,
            mailAddressConfirm: null,
            sharerList: [],
            residentPlace: null,
            ownershipTransferDate: null,
            zipCode: null,
            prefectureCd: null,
            address2: null,
            address3: null,
            condominiumName: null,
            roomNumber: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
        this.handleBackLinkOnClick = this.handleBackLinkOnClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            await this.getCurrentDateTime()
            await this.setInputHistory()
            await this.setApplyInfo()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({
                isLoadCompleted: true,
            })
        }
    }

    async getCurrentDateTime(): Promise<void> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
    }

    async setInputHistory(): Promise<void> {
        const {inputHistory} = this.props.location.state

        this.setState({
            familyName: inputHistory?.outlineInfo?.familyName ?? null,
            firstName: inputHistory?.outlineInfo?.firstName ?? null,
            familyNameKana: inputHistory?.outlineInfo?.familyNameKana ?? null,
            firstNameKana: inputHistory?.outlineInfo?.firstNameKana ?? null,
            mailAddress: inputHistory?.outlineInfo?.mailAddress ?? null,
            mailAddressConfirm: null,
            sharerList: inputHistory?.outlineInfo?.sharer ?? [],
            residentPlace: inputHistory?.outlineInfo?.residentPlace ?? null,
        })
    }

    async setApplyInfo(): Promise<void> {
        const {condominiumInfo, ownershipTransferDate} = this.props.location.state

        if (condominiumInfo && ownershipTransferDate) {
            const defaultOwnershipTransferDate = ownershipTransferDate
            const defaultZipCode = condominiumInfo.zipCode
            const defaultPrefectureCd = condominiumInfo.prefectureCd
            const defaultAddress2 = condominiumInfo.address2
            const defaultAddress3 = condominiumInfo.address3
            const defaultCondominiumName = condominiumInfo.condominiumName
            const defaultRoomNumber = condominiumInfo.roomNumber

            this.setState({
                ownershipTransferDate: defaultOwnershipTransferDate ?? null,
                zipCode: defaultZipCode ?? null,
                prefectureCd: defaultPrefectureCd ?? null,
                address2: defaultAddress2 ?? null,
                address3: defaultAddress3 ?? null,
                condominiumName: defaultCondominiumName ?? null,
                roomNumber: defaultRoomNumber ?? null,
            })
        }
    }

    async onClickApplyConfirmButton(request: NatPutOwnershipTransferNewMemberOutlineRequest): Promise<void> {
        const {ownershipTransfersRepo, condominiumCd} = this.props
        const {inputHistory} = this.props.location.state
        const beforeResidentPlace = this.props.location.state.inputHistory?.outlineInfo?.residentPlace ?? null

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutOwnershipTransferNewMemberResponse> =
                await this.props.asyncCallFunction(
                    ownershipTransfersRepo.applyOwnershipTransferNewMemberOutline.bind(
                        ownershipTransfersRepo, condominiumCd, request),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                    },
                    {
                        defaultErrorTitle: Messages.EOPCR0014.toString(),
                        defaultErrorTitlePriority: true,
                        replacementDetailErrorMessages: [{
                            targetMessageId: Messages.EOPCA0004,
                            replacementMessageId: Messages.EOPCR0015,
                        }],
                    },
                )

            const changeResidentPlace: boolean = request.residentPlace !== beforeResidentPlace

            if (response && response.requestError === null) {
                const locationState: NatGuestOwnershipTransferNewMemberLocationState = {
                    inputHistory: {
                        outlineInfo: {
                            familyName: request.familyName,
                            firstName: request.firstName,
                            familyNameKana: request.familyNameKana,
                            firstNameKana: request.firstNameKana,
                            mailAddress: request.mailAddress,
                            mailAddressConfirm: request.mailAddressConfirm,
                            sharer: request.sharer,
                            residentPlace: request.residentPlace,
                        },
                        detailInfo: (inputHistory?.detailInfo) ? {
                            moveInDate: changeResidentPlace ? null : inputHistory.detailInfo.moveInDate,
                            contactInformation:
                                changeResidentPlace ? null : inputHistory.detailInfo.contactInformation,
                            beforeContactAddress:
                                changeResidentPlace ? null : inputHistory.detailInfo.beforeContactAddress,
                            proxyContactAddress:
                                changeResidentPlace ? null : inputHistory.detailInfo.proxyContactAddress,
                            rentExistence: changeResidentPlace ? null : inputHistory.detailInfo.rentExistence,
                            paymentExistence: inputHistory.detailInfo.paymentExistence,
                            paymentContactAddress: inputHistory.detailInfo.paymentContactAddress,
                            destinationExistence: inputHistory.detailInfo.destinationExistence,
                            destinationContactAddress: inputHistory.detailInfo.destinationContactAddress,
                            version: inputHistory.detailInfo.version,
                        } : null,
                    },
                    applyId: this.props.location.state?.applyId,
                    ownershipTransferDate: this.state.ownershipTransferDate,
                    condominiumInfo: {
                        zipCode: this.state.zipCode,
                        prefectureCd: this.state.prefectureCd,
                        address2: this.state.address2,
                        address3: this.state.address3,
                        condominiumName: this.state.condominiumName,
                        roomNumber: this.state.roomNumber,
                    },
                }
                this.props.navigate(
                    `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/new-member/detail/${this.props.applyId ?? ''}`,
                    {
                        replace: true,
                        state: JSON.parse(JSON.stringify(locationState)),
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleBackLinkOnClick(): void {
        const locationState: NatGuestOwnershipTransferNewMemberLocationState = this.props.location.state
        this.props.navigate(
            `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/new-member/paymentChangeDateConfirm`,
            {
                replace: true,
                state: JSON.parse(JSON.stringify(locationState)),
            },
        )
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return <></>
        }

        return (
            <>
                <GuestPlainLinkStyledBackButton onClick={this.handleBackLinkOnClick}>戻る</GuestPlainLinkStyledBackButton>
                <BlockSpacer height={'15px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    組合員資格変更届（１/２）
                </PageTitle>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatGuestNewMemberOutlinePageForm
                    {...this.props}
                    {...this.state}
                    applyId={this.props.location.state?.applyId}
                    submitButtonLabel={'次へ進む'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
                <Box height={'30px'}/>
                <GuestBackButton onClick={this.handleBackLinkOnClick}>戻る</GuestBackButton>
                <Box height={'15px'}/>
            </>
        )
    }
}

export default createGuestWithErrorHandlingPage(NatGuestNewMemberInputOutlinePage)
